import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared, type ErrorView } from '@krp/client-runtime'

import type { CommandStatus } from '../../../shared/model/commandOutcome'
import { createKey, normalizeRid, ownedBy, sessionKey } from './keys'
import { acknowledge } from './rows'
import { warehouseChoiceDropped, warehouseChosen } from './warehouseChoiceSlice'
import type {
  ClearOperation,
  CreateOperation,
  InventoryIntentState,
  RowChange,
  SessionDelta,
  SessionDraft,
  SessionRow,
} from './types'

/**
 * Намерение инвентаризации: выбранный склад в каждой базе и НЕСОХРАНЁННАЯ
 * дельта сессии склада. Сама сессия — на сервере (срок `account`) и в кэше
 * RTK Query; здесь только то, что человек сделал и что ещё не подтверждено
 * сервером, плюс идентичность пишущих команд. Срок слайса — `screen`: пока
 * дельта не сохранена, окно предупреждает об уходе со страницы.
 */
const initialState: InventoryIntentState = { drafts: {}, scans: {}, creates: {}, clears: {}, droppedWarehouses: {} }

interface SessionRef {
  resourceId: string
  warehouseRefKey: string
}

const IDLE_SAVE: SessionDraft['save'] = { status: 'idle', sentRevision: 0, operationId: null, error: null, savedAt: null }

function draftOf(state: InventoryIntentState, ref: SessionRef): SessionDraft {
  const key = sessionKey(ref.resourceId, ref.warehouseRefKey)
  const existing = state.drafts[key]
  if (existing !== undefined) return existing
  const draft: SessionDraft = {
    resourceId: normalizeRid(ref.resourceId),
    warehouseRefKey: ref.warehouseRefKey.trim().toLowerCase(),
    // Свежие объекты: общий EMPTY_DELTA заморожен immer-ом после первого же действия.
    added: [],
    changes: {},
    removed: [],
    revision: 0,
    save: IDLE_SAVE,
  }
  state.drafts[key] = draft
  return state.drafts[key]
}

/** Правка сделана: сохранение ждёт своей очереди (автосохранение по паузе). */
function touched(draft: SessionDraft) {
  draft.revision += 1
  if (draft.save.status !== 'sending') draft.save = { ...draft.save, status: 'pending' }
}

function change(draft: SessionDraft, rowId: string, patch: RowChange) {
  draft.changes[rowId] = { ...(draft.changes[rowId] ?? {}), ...patch }
  touched(draft)
}

const inventorySlice = createSlice({
  name: 'onec/inventory',
  initialState,
  reducers: {
    scanStarted(state, action: PayloadAction<SessionRef>) {
      state.scans[sessionKey(action.payload.resourceId, action.payload.warehouseRefKey)] = { busy: true, error: null }
    },
    scanSettled(state, action: PayloadAction<SessionRef & { error: ErrorView | null }>) {
      const key = sessionKey(action.payload.resourceId, action.payload.warehouseRefKey)
      if (state.scans[key] === undefined) return
      state.scans[key] = { busy: false, error: action.payload.error }
    },

    /** Новая строка — скан или подбор. Новые сверху: так идут по полке. */
    rowAdded(state, action: PayloadAction<SessionRef & { row: SessionRow }>) {
      const draft = draftOf(state, action.payload)
      draft.added = [action.payload.row, ...draft.added]
      touched(draft)
    },
    countChanged(state, action: PayloadAction<SessionRef & { rowId: string; counted: number | null }>) {
      change(draftOf(state, action.payload), action.payload.rowId, { counted: action.payload.counted })
    },
    reprintSet(state, action: PayloadAction<SessionRef & { rowId: string; reprint: boolean }>) {
      change(draftOf(state, action.payload), action.payload.rowId, { reprint: action.payload.reprint })
    },
    /** Удаление строки — жестом или кнопкой; без подтверждающего окна (решение 264). */
    rowRemoved(state, action: PayloadAction<SessionRef & { rowId: string }>) {
      const draft = draftOf(state, action.payload)
      const id = action.payload.rowId
      draft.removed = draft.removed.includes(id) ? draft.removed : [...draft.removed, id]
      delete draft.changes[id]
      touched(draft)
    },
    /**
     * Строку «не найден» заменил созданный товар. Новая строка, а не правка
     * старой: сервер не переписывает снимок скана у сохранённой строки.
     */
    rowReplaced(state, action: PayloadAction<SessionRef & { oldId: string; row: SessionRow }>) {
      const draft = draftOf(state, action.payload)
      const { oldId, row } = action.payload
      const index = draft.added.findIndex((item) => item.id === oldId)
      draft.added = index < 0 ? [row, ...draft.added] : draft.added.map((item, at) => (at === index ? row : item))
      // Всегда в удалённые: старая строка могла уже уйти текущим сохранением и лечь на сервер.
      if (!draft.removed.includes(oldId)) draft.removed = [...draft.removed, oldId]
      delete draft.changes[oldId]
      touched(draft)
    },

    saveStarted(state, action: PayloadAction<SessionRef & { operationId: string; revision: number }>) {
      const draft = state.drafts[sessionKey(action.payload.resourceId, action.payload.warehouseRefKey)]
      if (draft === undefined) return
      draft.save = { ...draft.save, status: 'sending', operationId: action.payload.operationId, sentRevision: action.payload.revision, error: null }
    },
    /** Сервер подтвердил набор: отправленное уходит из дельты, правки во время сохранения остаются. */
    saveSucceeded(state, action: PayloadAction<SessionRef & { operationId: string; sent: SessionDelta; savedAt: string }>) {
      const key = sessionKey(action.payload.resourceId, action.payload.warehouseRefKey)
      const draft = state.drafts[key]
      if (draft === undefined || draft.save.operationId !== action.payload.operationId) return
      const left = acknowledge(draft, action.payload.sent)
      draft.added = left.added
      draft.changes = left.changes
      draft.removed = left.removed
      const behind = draft.revision > draft.save.sentRevision
      draft.save = { ...draft.save, status: behind ? 'pending' : 'idle', error: null, savedAt: action.payload.savedAt }
    },
    saveFailed(state, action: PayloadAction<SessionRef & { operationId: string; status: 'failed' | 'unknown'; error: ErrorView }>) {
      const draft = state.drafts[sessionKey(action.payload.resourceId, action.payload.warehouseRefKey)]
      if (draft === undefined || draft.save.operationId !== action.payload.operationId) return
      draft.save = { ...draft.save, status: action.payload.status, error: action.payload.error }
    },

    operationStarted(state, action: PayloadAction<{ kind: 'create'; operation: CreateOperation } | { kind: 'clear'; operation: ClearOperation }>) {
      const { key } = action.payload.operation
      const current = action.payload.kind === 'create' ? state.creates[key] : state.clears[key]
      // Пока прежняя команда в пути или её исход неизвестен, новую не начинаем: двойная запись.
      if (current !== undefined && (current.status === 'sending' || current.status === 'unknown')) return
      if (action.payload.kind === 'create') state.creates[key] = action.payload.operation
      else state.clears[key] = action.payload.operation
    },
    /** Ревизия базы прочитана: команда готова к отправке. Только своей операции. */
    operationPrepared(state, action: PayloadAction<{ key: string; operationId: string; expectedConfigurationVersion: string }>) {
      const operation = state.creates[action.payload.key] ?? state.clears[action.payload.key]
      if (operation === undefined || operation.operationId !== action.payload.operationId) return
      operation.expectedConfigurationVersion = action.payload.expectedConfigurationVersion
    },
    operationSettled(
      state,
      action: PayloadAction<{
        key: string
        operationId: string
        status: Exclude<CommandStatus, 'sending'>
        resendable: boolean
        error: ErrorView | null
      }>,
    ) {
      const operation = state.creates[action.payload.key] ?? state.clears[action.payload.key]
      if (operation === undefined || operation.operationId !== action.payload.operationId) return
      operation.status = action.payload.status
      operation.resendable = action.payload.resendable
      operation.error = action.payload.error
    },
    operationResent(state, action: PayloadAction<{ key: string }>) {
      const operation = state.creates[action.payload.key] ?? state.clears[action.payload.key]
      if (operation === undefined) return
      operation.status = 'sending'
      operation.error = null
    },
    /** Скрыть итог. Неизвестный исход не скрывается: забыть ключ — потерять способ проверить. */
    operationDismissed(state, action: PayloadAction<{ key: string }>) {
      const operation = state.creates[action.payload.key] ?? state.clears[action.payload.key]
      if (operation === undefined || operation.status === 'sending' || operation.status === 'unknown') return
      delete state.creates[action.payload.key]
      delete state.clears[action.payload.key]
    },

    /** Сессия очищена на сервере: дельта и итоги создания этого склада больше не о чём. */
    sessionCleared(state, action: PayloadAction<SessionRef>) {
      const key = sessionKey(action.payload.resourceId, action.payload.warehouseRefKey)
      delete state.drafts[key]
      delete state.scans[key]
      for (const id of Object.keys(state.creates)) if (id.startsWith(`${key}|`)) delete state.creates[id]
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      const rid = action.payload.resourceId
      for (const bucket of [state.drafts, state.scans, state.creates, state.clears] as Record<string, unknown>[]) {
        for (const key of Object.keys(bucket)) if (ownedBy(key, rid)) delete bucket[key]
      }
      delete state.droppedWarehouses[normalizeRid(rid)]
    })
    // Почему снят выбор склада, окно говорит до следующего выбора в этой базе.
    builder.addCase(warehouseChoiceDropped, (state, action) => {
      state.droppedWarehouses[normalizeRid(action.payload.resourceId)] = action.payload.warehouse.name
    })
    builder.addCase(warehouseChosen, (state, action) => {
      delete state.droppedWarehouses[normalizeRid(action.payload.resourceId)]
    })
  },
})

export const {
  scanStarted,
  scanSettled,
  rowAdded,
  countChanged,
  reprintSet,
  rowRemoved,
  rowReplaced,
  saveStarted,
  saveSucceeded,
  saveFailed,
  operationStarted,
  operationPrepared,
  operationSettled,
  operationResent,
  operationDismissed,
  sessionCleared,
} = inventorySlice.actions

export const inventoryReducer = inventorySlice.reducer

/** Действия, после которых дельта ждёт автосохранения. */
export const EDIT_ACTIONS = [rowAdded, countChanged, reprintSet, rowRemoved, rowReplaced] as const

export { createKey }
